import { ChangeDetectionStrategy, Component, input, computed, inject, signal, viewChild } from '@angular/core';
import { TableDisplay } from '../../../models/runnable_report';
import { MatTableModule } from '@angular/material/table';
import { MatSort, MatSortModule, Sort } from '@angular/material/sort';
import { MatPaginator, MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { ReportRunState } from '../report-run-state';
import { DatePipe } from '@angular/common';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { CsvDownloadDialog, CsvDownloadDialogData } from './csv-download-dialog';
import { logger, RisrPaginatorDirective, RoundPipe } from '@fry-it/risr-core';
import { RouterLink } from '@angular/router';
import { DOC_VIEW_HANDLER, DocViewDocType } from './doc-view-handler';

@Component({
  selector: 'risr-features-reports-report-table',
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatSortModule,
    MatButton,
    DatePipe,
    RisrPaginatorDirective,
    RoundPipe,
    RouterLink,
  ],
  templateUrl: './report-table.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    .header-row {
      align-items: center;
      background-color: #fff;
      display: flex;
      gap: 2em;
      justify-content: space-between;
      margin-top: 1em;
      padding: 0.64em;
    }

    .total {
      margin: 10px;
    }

    .table-container {
      display: block;
      width: 100%;
      overflow-x: auto;
    }

    table {
      width: 100%;
    }

    .mat-mdc-header-cell,
    .mat-sort-header-content {
      font-weight: bold;
    }


    th.mat-sort-header-sorted {
      color: inherit;
    }
    `,
})
export class ReportTable {
    reportDisplayItem = input.required<TableDisplay>();

    reportRunState = inject(ReportRunState);
    private readonly dialog = inject(MatDialog);
    private readonly docViewHandler = inject(DOC_VIEW_HANDLER, { optional: true });

    matSort = viewChild<MatSort>(MatSort);
    matPaginator = viewChild<MatPaginator>(MatPaginator);

    currentSort = computed(() => {
      const sort = this.matSort();
      if (!sort) {
        return undefined;
      }
      if (!sort.active || !sort.direction) {
        return undefined;
      }
      return {
        field: sort.active,
        direction: sort.direction,
      }
    })

    precision = computed(() => {
      const display = this.reportDisplayItem();
      return display.options?.defaultPrecision ?? 2;
    })

    currentPagination = computed(() => {
      const paginator = this.matPaginator();
      if (!paginator) {
        return undefined;
      }
      return {
        size: paginator.pageSize,
        start: paginator.pageIndex * paginator.pageSize,
      }
    })

    reportTableData = computed(() => {
        const name = this.reportDisplayItem().output;
        const result = this.reportRunState.getOutputResult(name)();
        return result;
    });

    dataSource = computed(() => {
      const result = this.reportTableData();
      if (!result) {
        return [];
      }
      const headers = result.headers;
      const data = result.data;
      return data.map(row => {
        return row.reduce<Record<string, unknown>>((acc, cell, index) => {
          const header = headers[index];
          if (!header) {
            return acc;
          }
          acc[header.id] = cell;
          return acc;
        }, {})
      })
    });

    displayedColumns = computed(() => {
      const result = this.reportTableData();
      if (!result) {
        return [];
      }
      return result.headers.map(h => h);
    });

    displayedColumnsId = computed(() => {
      return this.displayedColumns().map(h => h.id);
    });

    pagination = computed(() => {
      const result = this.reportTableData();
      if (!result) {
        return {
          size: 0,
          start: 0,
          total: 0,
        };
      }
      return {
        size: result.size,
        start: result.start,
        total: result.total,
      };
    });

    showCsvDownload = computed(() => {
      const display = this.reportDisplayItem();
      return display.options?.showCsvDownload ?? false;
    });

    noPagination = computed(() => {
      const display = this.reportDisplayItem();
      return display.options?.noPagination ?? false;
    });

    showEmbeddedWarning = computed(() => {
      const showType = this.reportRunState.options().showType;
      if (showType !== "embedded") {
        return false;
      }

      const pagination = this.pagination();
      return pagination.total > pagination.size;
    });

    showTotalNumber = computed(() => {
      return this.reportRunState.options().showType !== "widget" && this.pagination().total > 0;
    });

    showHeaderRow = computed(() => {
      return this.showEmbeddedWarning() || this.showTotalNumber() || this.showCsvDownload();
    })

    downloadCsv() {
      this.dialog.open(CsvDownloadDialog, {
        width: '400px',
        disableClose: true,
        data: {
          outputName: this.reportDisplayItem().id,
          reportRunState: this.reportRunState,
        } satisfies CsvDownloadDialogData,
      });
    }

    pageLoading = signal(false);

    async onPageChange(event: PageEvent) {
      const currentSort = this.currentSort();
      const pagination = {
        size: event.pageSize,
        start: event.pageIndex * event.pageSize,
      }
      this.pageLoading.set(true);
      try {
        await this.reportRunState.loadOutputResult(this.reportDisplayItem(), pagination, currentSort);
      } catch (error) {
        logger.error('Failed to load page', error instanceof Error ? error : new Error(String(error)));
      } finally {
        this.pageLoading.set(false);
      }
    }

    async onSortChange(event: Sort) {
      const currentPagination = this.currentPagination();
      const sort = event.direction ? {
        field: event.active,
        direction: event.direction,
      } : undefined;
      this.pageLoading.set(true);
      try {
        await this.reportRunState.loadOutputResult(this.reportDisplayItem(), currentPagination, sort);
      } catch (error) {
        logger.error('Failed to sort', error instanceof Error ? error : new Error(String(error)));
      } finally {
        this.pageLoading.set(false);
      }
    }

    protected readonly hasDocViewHandler = computed(() => !!this.docViewHandler);

    openDocPreview(docType: string | undefined, id: string) {
      if (docType) {
        this.docViewHandler?.openPreview(docType as DocViewDocType, id);
      }
    }

    getDocLink(docType: string | undefined, id: string): string[] {
      if (this.docViewHandler && docType) {
        return this.docViewHandler.getLink(docType as DocViewDocType, id);
      }
      return ['.'];
    }
}
